import { useState, type FormEvent } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api } from '../../api/client';
import { endpoints } from '../../api/endpoints';
import { useBranches } from '../../hooks/useBranches';
import { useKeyboardForm } from '../../hooks/useKeyboardForm';
import { usePermission } from '../../hooks/usePermission';
import { date, fine, today } from '../../utils/format';
import { Empty, ErrorNotice, Loading, PageHeader, Table } from '../../components/ui';
import { DateInput, Drawer, Field, Select, TextInput } from '../../components/ui/Form';
import { VoidDialog } from '../../components/ui/VoidDialog';

interface TransferRow {
  id: string;
  doc_no: string;
  doc_date: string;
  status: string;
  from_branch: string;
  to_branch: string;
  pieces: string;
}

interface TransferLine {
  id: string;
  item_code: string;
  dispatched_net_wt: string;
  dispatched_fine_wt: string;
  received_fine_wt: string | null;
  variance_fine_wt: string | null;
}

interface TransferDetail {
  id: string;
  doc_no: string;
  status: string;
  lines: TransferLine[];
}

interface Item {
  id: string;
  item_code: string;
  product_name: string;
  fine_wt: string;
  branch_name: string;
}

const STATUS_TONE: Record<string, string> = {
  REQUESTED: 'text-gold-700',
  APPROVED: 'text-gold-700',
  DISPATCHED: 'text-gold-700',
  RECEIVED: 'text-success',
  RECEIVED_WITH_VARIANCE: 'text-danger',
  CANCELLED: 'text-ink-soft',
};

/**
 * Moving stock between branches.
 *
 * Between dispatch and receipt the metal sits in a virtual transit branch, so
 * it is never in two places at once and never in none. A weight difference on
 * arrival raises an adjustment rather than being absorbed quietly.
 */
export function TransfersPage() {
  const { can } = usePermission();
  const client = useQueryClient();
  const onEnter = useKeyboardForm();
  const { branches, defaultBranchId } = useBranches();

  const [open, setOpen] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState<{ id: string; docNo: string } | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [form, setForm] = useState({ fromBranchId: '', toBranchId: '', docDate: today(), remarks: '' });
  const [chosen, setChosen] = useState<string[]>([]);
  const [weights, setWeights] = useState<Record<string, string>>({});

  const list = useQuery({ queryKey: ['transfers'], queryFn: () => api.get<{ data: TransferRow[] }>('/transfers') });
  const items = useQuery({
    queryKey: ['items', 'IN_STOCK', form.fromBranchId],
    queryFn: () =>
      endpoints.inventory.items({ status: 'IN_STOCK', branchId: form.fromBranchId || undefined, limit: '200' }),
    enabled: open,
  });
  const detail = useQuery({
    queryKey: ['transfer', detailId],
    queryFn: () => api.get<TransferDetail>(`/transfers/${detailId}`),
    enabled: detailId !== null,
  });

  const itemList = (items.data?.data ?? []) as unknown as Item[];

  const act = useMutation({
    mutationFn: ({ id, action, body }: { id: string; action: string; body?: unknown }) =>
      api.post(`/transfers/${id}/${action}`, body ?? {}),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['transfers'] });
      void client.invalidateQueries({ queryKey: ['transfer'] });
      void client.invalidateQueries({ queryKey: ['items'] });
    },
    onError: setError,
  });

  const create = useMutation({
    mutationFn: () =>
      api.post(
        '/transfers',
        {
          fromBranchId: form.fromBranchId || defaultBranchId,
          toBranchId: form.toBranchId,
          docDate: form.docDate,
          itemIds: chosen,
          ...(form.remarks ? { remarks: form.remarks } : {}),
        },
        { 'idempotency-key': crypto.randomUUID() },
      ),
    onSuccess: () => {
      setOpen(false);
      setChosen([]);
      setError(null);
      void client.invalidateQueries({ queryKey: ['transfers'] });
    },
    onError: setError,
  });

  const submit = (e: FormEvent): void => {
    e.preventDefault();
    create.mutate();
  };

  const toggle = (id: string): void => setChosen((c) => (c.includes(id) ? c.filter((x) => x !== id) : [...c, id]));

  const rows = list.data?.data ?? [];
  const chosenFine = itemList.filter((i) => chosen.includes(i.id)).reduce((a, i) => a + Number(i.fine_wt), 0);

  return (
    <>
      <PageHeader
        title="Branch transfers"
        subtitle="Stock in motion sits in transit until it is received, so it is never in two branches or in none."
        actions={
          can('stock.transfer_request') && branches.length > 1 ? (
            <button className="btn-primary" onClick={() => setOpen(true)}>
              New transfer
            </button>
          ) : null
        }
      />
      <ErrorNotice error={error} />

      {branches.length < 2 ? (
        <Empty message="Transfers need more than one branch." />
      ) : (
        <>
          {list.isLoading ? <Loading /> : null}
          {!list.isLoading && rows.length === 0 ? <Empty message="No transfers yet." /> : null}

          {rows.length > 0 ? (
            <Table head={['Date', 'Document', 'From', 'To', 'Pieces', 'Status', '']}>
              {rows.map((t) => (
                <tr key={t.id}>
                  <td className="td">{date(t.doc_date)}</td>
                  <td className="td num cursor-pointer underline" onClick={() => setDetailId(t.id)}>
                    {t.doc_no}
                  </td>
                  <td className="td">{t.from_branch}</td>
                  <td className="td">{t.to_branch}</td>
                  <td className="td num text-right">{t.pieces}</td>
                  <td className={`td text-xs ${STATUS_TONE[t.status] ?? ''}`}>
                    {t.status.replace(/_/g, ' ').toLowerCase()}
                  </td>
                  <td className="td">
                    <div className="flex gap-2">
                      {t.status === 'REQUESTED' && can('stock.transfer_approve') ? (
                        <button
                          className="btn-ghost py-1 text-xs"
                          onClick={() => act.mutate({ id: t.id, action: 'approve' })}
                        >
                          Approve
                        </button>
                      ) : null}
                      {t.status === 'APPROVED' && can('stock.transfer_request') ? (
                        <button
                          className="btn-ghost py-1 text-xs"
                          onClick={() => act.mutate({ id: t.id, action: 'dispatch' })}
                        >
                          Dispatch
                        </button>
                      ) : null}
                      {(t.status === 'REQUESTED' || t.status === 'APPROVED') && can('stock.transfer_approve') ? (
                        <button
                          className="btn-ghost py-1 text-xs text-danger"
                          onClick={() => setCancelling({ id: t.id, docNo: t.doc_no })}
                        >
                          Cancel
                        </button>
                      ) : null}
                      {t.status === 'DISPATCHED' && can('stock.transfer_receive') ? (
                        <button className="btn-primary py-1 text-xs" onClick={() => setDetailId(t.id)}>
                          Receive
                        </button>
                      ) : null}
                    </div>
                  </td>
                </tr>
              ))}
            </Table>
          ) : null}
        </>
      )}

      {open ? (
        <Drawer
          title="New transfer"
          subtitle="Choose the pieces leaving. Somebody other than you has to approve it before it can be dispatched."
          onClose={() => setOpen(false)}
        >
          <ErrorNotice error={error} />
          <form autoComplete="off" onKeyDown={onEnter} onSubmit={submit} className="space-y-4">
            <div className="grid grid-cols-2 gap-3">
              <Field label="From" required>
                <Select
                  value={form.fromBranchId || (defaultBranchId ?? '')}
                  onChange={(v) => {
                    setForm({ ...form, fromBranchId: v });
                    setChosen([]);
                  }}
                  options={branches.map((b) => ({ value: b.id, label: b.name }))}
                />
              </Field>
              <Field label="To" required>
                <Select
                  value={form.toBranchId}
                  onChange={(v) => setForm({ ...form, toBranchId: v })}
                  options={branches
                    .filter((b) => b.id !== (form.fromBranchId || defaultBranchId))
                    .map((b) => ({ value: b.id, label: b.name }))}
                />
              </Field>
            </div>

            <Field label="Date" required>
              <DateInput value={form.docDate} onChange={(v) => setForm({ ...form, docDate: v })} />
            </Field>

            <Field label="Remarks">
              <TextInput value={form.remarks} onChange={(v) => setForm({ ...form, remarks: v })} />
            </Field>

            <div>
              <p className="label">Pieces to send</p>
              {items.isLoading ? <Loading /> : null}
              <div className="max-h-72 space-y-1 overflow-y-auto rounded border border-ink/15 bg-white p-2">
                {itemList.length === 0 ? (
                  <p className="p-2 text-sm text-ink-soft">Nothing in stock at that branch.</p>
                ) : (
                  itemList.map((i) => (
                    <label key={i.id} className="flex items-center gap-2 rounded p-1 text-sm hover:bg-gold-50">
                      <input type="checkbox" checked={chosen.includes(i.id)} onChange={() => toggle(i.id)} />
                      <span className="num">{i.item_code}</span>
                      <span className="text-ink-soft">{i.product_name}</span>
                      <span className="num ml-auto">{fine(i.fine_wt)}</span>
                    </label>
                  ))
                )}
              </div>
              {chosen.length > 0 ? (
                <p className="mt-2 text-sm">
                  {chosen.length} piece{chosen.length === 1 ? '' : 's'},{' '}
                  <span className="num font-medium">{fine(chosenFine.toFixed(4))}</span>
                </p>
              ) : null}
            </div>

            <button
              className="btn-primary w-full"
              disabled={chosen.length === 0 || !form.toBranchId || create.isPending}
            >
              {create.isPending ? 'Raising…' : 'Raise transfer'}
            </button>
          </form>
        </Drawer>
      ) : null}

      {detailId && detail.data ? (
        <Drawer
          title={`Transfer ${detail.data.doc_no}`}
          subtitle={detail.data.status.replace(/_/g, ' ').toLowerCase()}
          onClose={() => {
            setDetailId(null);
            setWeights({});
          }}
        >
          <ErrorNotice error={error} />

          <table className="w-full text-sm">
            <thead>
              <tr className="border-y border-ink/20 text-xs uppercase tracking-wide text-ink-soft">
                <th className="py-1 text-left">Item</th>
                <th className="py-1 text-right">Sent</th>
                <th className="py-1 text-right">
                  {detail.data.status === 'DISPATCHED' ? 'Weigh on arrival' : 'Received'}
                </th>
              </tr>
            </thead>
            <tbody>
              {detail.data.lines.map((l) => (
                <tr key={l.id} className="border-b border-ink/10">
                  <td className="num py-1">{l.item_code}</td>
                  <td className="num py-1 text-right">{fine(l.dispatched_fine_wt)}</td>
                  <td className="py-1 text-right">
                    {detail.data.status === 'DISPATCHED' ? (
                      <input
                        className="field num w-28 px-2 py-1 text-right text-sm"
                        inputMode="decimal"
                        placeholder={l.dispatched_fine_wt}
                        value={weights[l.id] ?? ''}
                        onChange={(e) => setWeights({ ...weights, [l.id]: e.target.value.replace(/[^\d.]/g, '') })}
                      />
                    ) : (
                      <span className="num">
                        {l.received_fine_wt ? fine(l.received_fine_wt) : '—'}
                        {l.variance_fine_wt && Number(l.variance_fine_wt) !== 0 ? (
                          <span className="block text-xs text-danger">
                            {Number(l.variance_fine_wt) > 0 ? '+' : ''}
                            {l.variance_fine_wt} g
                          </span>
                        ) : null}
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>

          {detail.data.status === 'DISPATCHED' && can('stock.transfer_receive') ? (
            <>
              <p className="mt-3 text-xs text-ink-soft">
                Leave a box empty to accept the weight it was sent at. Anything different raises an adjustment that
                somebody has to explain and somebody else has to approve.
              </p>
              <button
                className="btn-primary mt-3 w-full"
                disabled={act.isPending}
                onClick={() =>
                  act.mutate({
                    id: detail.data.id,
                    action: 'receive',
                    body: {
                      weights: detail.data.lines
                        .filter((l) => weights[l.id])
                        .map((l) => ({
                          lineId: l.id,
                          receivedFineWt: weights[l.id]!,
                          receivedNetWt: l.dispatched_net_wt,
                        })),
                    },
                  })
                }
              >
                {act.isPending ? 'Receiving…' : 'Receive into this branch'}
              </button>
            </>
          ) : null}
        </Drawer>
      ) : null}

      <VoidDialog
        docNo={cancelling?.docNo ?? null}
        what="transfer"
        effect="Nothing has left the branch yet, so the pieces simply become free to sell, melt or send again."
        busy={act.isPending}
        onCancel={() => setCancelling(null)}
        onConfirm={(reason) =>
          act.mutate(
            { id: cancelling!.id, action: 'cancel', body: { reason } },
            { onSuccess: () => setCancelling(null) },
          )
        }
      />
    </>
  );
}
